import { ValidatorFn, Validators } from '@angular/forms';
import { Shop } from '@app/services/shops/shop.model';
import { CountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { FieldType } from '../../../services/settings/settings.service';

export interface VerificationField {
  title?: string;
  description?: string;
  warnText?: string;
  type?: FieldType;
  value?: boolean;
  default?: any;
  dataName?: string;
  validators?: ValidatorFn[];
  stringValue?: string;
  displayCondition?: (countryCode: CountryCode, shop?: Shop) => boolean;
  disabledWhen?: (shop: Shop) => boolean;
  validationMsg?: string;
  tooltip?: string;
  additionalInfo?: { name: string; type?: FieldType; data: VerificationField[] };
}

export interface modifiableValue {
  path: string;
  value: string;
}

export const verificationFields: VerificationField[] = [
  {
    title: 'Missing address',
    description: 'Flag orders without a delivery address',
    warnText: 'Address missing',
  },
  {
    title: 'Incorrect address',
    description: 'Flag orders with incorrect or unverifiable addresses',
    warnText: 'Fix address',
    additionalInfo: {
      name: 'address',
      type: FieldType.CHECKBOX,
      data: [
        {
          description: 'Flag PO box addresses',
          default: false,
          dataName: 'po_box',
          displayCondition: (countryCode: CountryCode, shop: Shop) => {
            if (!countryCode) return false;
            return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
          },
        },
        {
          description: 'Ignore city check',
          default: '',
          dataName: 'validationAdjustments',
          stringValue: 'ignore_city',
          tooltip: `If checked, orders won't be checked for correct spelling of any city names`,
          displayCondition: (countryCode: CountryCode, shop: Shop) => {
            if (!countryCode) return false;
            return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
          },
        },
        {
          description: 'Ignore Address Line 2 check',
          default: false,
          dataName: 'disableAddressLine2Verification',
          tooltip: `If checked, orders won’t be flagged for missing addresses that are missing information like apartment or suite number`,
        },
        {
          description: 'Ignore invalid house numbers',
          default: false,
          dataName: 'disableHouseNumberVerification',
          tooltip: `If checked, orders won’t be flagged for addresses that are missing house numbers`,
        },
        {
          description: 'Skip re-check for repeat verified customers',
          default: false,
          dataName: 'skipReVerificationForCustomerAddress',
          tooltip: `When enabled, addresses that were manually marked as verified will not be re-validated for the same customer on future orders. Applies only if the address matches exactly`,
        },
      ],
    },
  },
  {
    title: 'Incorrect phone number',
    description: 'Flag orders with incorrect phone numbers',
    warnText: 'Fix phone',
    additionalInfo: {
      name: 'phone',
      type: FieldType.CHECKBOX,
      data: [
        {
          description: 'Ignore phone check on domestic orders',
          default: false,
          dataName: 'verifiedPhone',
          displayCondition: (countryCode: CountryCode, shop: Shop) => {
            if (!countryCode) return false;
            return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
          },
        },
      ],
    },
  },
  {
    title: 'Missing phone number',
    description: 'Flag orders with no phone number',
    warnText: 'Phone missing',
    dataName: 'flagMissingPhone',
  },
  {
    title: 'Unpaid orders',
    description: 'Require approval for unpaid orders',
    warnText: 'Pending payment',
    dataName: 'verifyPaymentStatus',
  },
  {
    title: 'Fraud risk',
    description: 'Require approval for medium or high-risk orders',
    warnText: 'Fraud risk',
    dataName: 'verifyFraudRisk',
    additionalInfo: {
      name: 'flagMediumFraudRisk',
      type: FieldType.CHECKBOX,
      data: [
        {
          description: 'Flag medium risk deliveries',
          dataName: 'flagMediumFraudRisk',
        },
      ],
    },
  },
  {
    title: 'Missing item weight',
    description: 'Flag orders without item weight details',
    warnText: 'Item weight missing',
    dataName: 'supportsMeasuresValidation',
    displayCondition: (countryCode: CountryCode, shop: Shop) => {
      if (!countryCode) return false;
      return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
    },
    disabledWhen: (shop: Shop) => {
      return shop.isCheckoutOnly;
    },
    additionalInfo: {
      name: 'weight',
      type: FieldType.CHECKBOX,
      data: [
        {
          description: 'Only check in Assign',
          default: false,
          dataName: 'flagWeightInAssign',
        },
      ],
    },
  },
  {
    title: 'Missing package size',
    description: 'Flag orders without package details',
    warnText: 'Package size missing',
    dataName: 'supportsPackageValidation',
    displayCondition: (countryCode: CountryCode, shop: Shop) => {
      if (!countryCode) return false;
      return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
    },
    disabledWhen: (shop: Shop) => {
      return shop.isCheckoutOnly;
    },
    additionalInfo: {
      name: 'package',
      type: FieldType.CHECKBOX,
      data: [
        {
          description: 'Only check in Assign',
          default: false,
          dataName: 'flagPackageInAssign',
        },
      ],
    },
  },
  {
    title: 'Weather alerts',
    description: 'Flag orders with potential delivery delays due to weather',
    warnText: 'Weather alert',
    dataName: 'isActiveWeather',
    displayCondition: (countryCode: CountryCode, shop?: Shop) => {
      if (!countryCode) return false;
      if (shop && shop.isCheckoutOnly) return false;
      return [CountryCode.US, CountryCode.CA].includes(countryCode) ? true : false;
    },
    additionalInfo: {
      name: 'weather',
      data: [
        {
          description: 'Min temp',
          dataName: 'weather-isActiveMinTemp',
        },
        {
          dataName: 'weather-minTemp',
          type: FieldType.TEMPERATURE_INPUT,
          validators: [Validators.max(200), Validators.min(-200)],
          validationMsg: 'Brr that temperature is too low (min temp)',
        },
        {
          description: 'Max temp',
          dataName: 'weather-isActiveMaxTemp',
        },
        {
          dataName: 'weather-maxTemp',
          type: FieldType.TEMPERATURE_INPUT,
          validators: [Validators.max(200), Validators.min(-200)],
          validationMsg: 'Ahh that temperature is too high (max temp)',
        },
        {
          description: 'Processing days before shipping to calculate (in business days)',
          dataName: 'weather-processingDays',
          type: FieldType.INPUT,
          validators: [Validators.max(10), Validators.min(0.5), Validators.pattern('^\\d+(\\.\\d+)*$')],
          validationMsg: 'Must be more than 0.5 and no greater than 10',
        },
      ],
    },
  },
  {
    title: 'Out of stock',
    description: 'Flag orders when items are out of stock across all inventories',
    warnText: 'Out of stock',
    dataName: 'verifyInventory',
    stringValue: 'location',
    additionalInfo: {
      name: 'inventoryDailyCheck',
      type: FieldType.CHECKBOX,
      data: [
        {
          description: 'Recheck daily for restock',
          value: false,
          dataName: 'inventoryPollingTime',
          stringValue: '06:00',
        },
      ],
    },
    displayCondition: (countryCode: CountryCode, shop: Shop) => {
      if (!countryCode) return false;
      return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
    },
  },
];
